import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { X, Play, FileText, Table2, FolderOpen, Link2 } from "lucide-react";
import "youtube-lite";

import { isYouTubeUrl } from "../utils/urlHelpers";
import { getLinkLabel } from "../utils/linkLabels";

const KIND_ICONS = {
  video: Play,
  doc: FileText,
  sheet: Table2,
  folder: FolderOpen,
  link: Link2,
};

const ensureUrlProtocol = (rawUrl = "") => {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }
  return `https://${trimmed}`;
};

const parseYouTubeTimestamp = (value) => {
  if (!value) return null;
  if (/^\d+$/.test(value)) {
    return parseInt(value, 10);
  }
  const match = value.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/i);
  if (!match) return null;
  const [, hours, minutes, seconds] = match;
  const totalSeconds =
    (parseInt(hours || "0", 10) || 0) * 3600 +
    (parseInt(minutes || "0", 10) || 0) * 60 +
    (parseInt(seconds || "0", 10) || 0);
  return totalSeconds > 0 ? totalSeconds : null;
};

const getYouTubeLiteConfig = (rawUrl) => {
  if (!rawUrl) return null;
  const normalized = ensureUrlProtocol(rawUrl);

  try {
    const parsedUrl = new URL(normalized);
    const hostname = parsedUrl.hostname.replace(/^www\./, "");
    let videoId = "";

    if (hostname === "youtu.be") {
      videoId = parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
    } else if (parsedUrl.searchParams.get("v")) {
      videoId = parsedUrl.searchParams.get("v");
    } else if (parsedUrl.pathname.startsWith("/shorts/")) {
      videoId = parsedUrl.pathname.split("/")[2] || "";
    } else if (parsedUrl.pathname.startsWith("/embed/")) {
      videoId = parsedUrl.pathname.split("/")[2] || "";
    } else if (parsedUrl.pathname.startsWith("/live/")) {
      videoId = parsedUrl.pathname.split("/")[2] || "";
    }

    if (!videoId) {
      return null;
    }

    const params = new URLSearchParams();
    const startParam =
      parsedUrl.searchParams.get("start") || parsedUrl.searchParams.get("t");
    const startSeconds = parseYouTubeTimestamp(startParam);
    if (startSeconds) {
      params.set("start", startSeconds.toString());
    }

    return {
      videoId,
      params: params.toString() || undefined,
    };
  } catch (error) {
    return null;
  }
};

const YouTubeLitePlayer = ({
  videoId,
  params,
  title = "YouTube video player",
}) => {
  if (!videoId) {
    return null;
  }

  return (
    <Box
      component="youtube-lite"
      video={videoId}
      {...(params ? { params } : {})}
      aria-label={title}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        border: 0,
        borderRadius: "inherit",
        overflow: "hidden",
      }}
    />
  );
};

export const DocumentationLink = ({
  url,
  label,
  variant = "text",
  sx = {},
}) => {
  const theme = useTheme();
  const [showVideo, setShowVideo] = useState(false);
  const normalizedUrl = useMemo(() => ensureUrlProtocol(url), [url]);
  const isYoutubeLink = normalizedUrl ? isYouTubeUrl(normalizedUrl) : false;
  const youtubeConfig = useMemo(
    () => (isYoutubeLink ? getYouTubeLiteConfig(normalizedUrl) : null),
    [isYoutubeLink, normalizedUrl]
  );
  const modalZIndex = Math.max(theme.zIndex?.modal ?? 1300, 10000) + 4;

  // Etiqueta legible derivada de la URL ("Vídeo", "Drive", "Seranking"...)
  // salvo que se pase una explícita.
  const derived = useMemo(() => getLinkLabel(normalizedUrl), [normalizedUrl]);
  const resolvedLabel = label ?? derived.label;
  const Icon = KIND_ICONS[isYoutubeLink ? "video" : derived.kind] || Link2;
  const isChip = variant === "chip";
  const linkColor = theme.custom?.colors?.link || "#0b63f4";

  const chipSx = isChip
    ? {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        maxWidth: 150,
        px: 0.75,
        py: 0.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
        fontSize: "0.7rem",
        fontWeight: 500,
        lineHeight: 1.7,
        textDecoration: "none",
        whiteSpace: "nowrap",
        "&:hover": {
          borderColor: linkColor,
          backgroundColor: theme.palette.action.selected,
          color: linkColor,
          textDecoration: "none",
        },
      }
    : {};

  const labelNode = isChip ? (
    <>
      <Icon size={12} style={{ flexShrink: 0 }} />
      <Box
        component="span"
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {resolvedLabel}
      </Box>
    </>
  ) : (
    resolvedLabel
  );

  const handleMouseDown = (e) => {
    e.stopPropagation();
  };

  const handleOpenVideo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (youtubeConfig?.videoId) {
      setShowVideo(true);
    }
  };

  const handleCloseVideo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowVideo(false);
  };

  if (!normalizedUrl) {
    return null;
  }

  if (isYoutubeLink && youtubeConfig?.videoId) {
    return (
      <>
        <Typography
          component="button"
          variant="caption"
          onClick={handleOpenVideo}
          onMouseDown={handleMouseDown}
          draggable={false}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            color: theme.custom?.colors?.link || "#0b63f4",
            fontWeight: 500,
            cursor: "pointer",
            transition: "color 0.2s ease-in-out, text-decoration 0.2s ease-in-out",
            userSelect: "none",
            textDecoration: "none",
            "&:hover": {
              color: theme.custom?.colors?.link || "#0b63f4",
              textDecoration: "underline",
              textDecorationColor: theme.custom?.colors?.link || "#0b63f4",
            },
            ...chipSx,
            ...sx,
          }}
        >
          {labelNode}
        </Typography>

        <Modal
          open={showVideo}
          onClose={handleCloseVideo}
          aria-labelledby="video-modal"
          disableAutoFocus
          slotProps={{
            root: {
              sx: { zIndex: modalZIndex },
            },
          }}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
          }}
          BackdropProps={{
            onClick: handleCloseVideo,
            sx: {
              zIndex: modalZIndex,
              backgroundColor: "rgba(15, 23, 42, 0.75)",
              backdropFilter: "blur(1px)",
            },
          }}
        >
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: { xs: 2, sm: 3, md: 4 },
              pointerEvents: "none",
              zIndex: modalZIndex + 1,
            }}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "800px",
                aspectRatio: "16/9",
                backgroundColor: "#000",
                borderRadius: 2,
                boxShadow: theme.shadows[24],
                outline: "none",
                overflow: "hidden",
                pointerEvents: "auto",
              }}
            >
              <IconButton
                onClick={handleCloseVideo}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  zIndex: 10000,
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                  },
                }}
              >
                <X size={24} />
              </IconButton>
              {showVideo && (
                <YouTubeLitePlayer
                  videoId={youtubeConfig.videoId}
                  params={youtubeConfig.params}
                />
              )}
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Box
      component="a"
      href={normalizedUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={handleMouseDown}
      draggable={false}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: theme.custom?.colors?.link || "#0b63f4",
        textDecoration: "none",
        fontSize: "0.75rem",
        fontWeight: 500,
        transition: "color 0.2s ease-in-out, text-decoration 0.2s ease-in-out",
        "&:hover": {
          color: theme.custom?.colors?.link || "#0b63f4",
          textDecoration: "underline",
          textDecorationColor: theme.custom?.colors?.link || "#0b63f4",
        },
        ...chipSx,
        ...sx,
      }}
    >
      {labelNode}
    </Box>
  );
};

DocumentationLink.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
  variant: PropTypes.oneOf(["text", "chip"]),
  sx: PropTypes.object,
};
